/**
 * Eldra IG reels — leggero:
 * - poster subito, src video solo near viewport
 * - max N video in play insieme
 * - loop solo sui primi secondi
 * - unload quando lontano
 */
(() => {
  if (window.__eldraIgReelsBound) return;
  window.__eldraIgReelsBound = true;

  const reduceMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ensureSource = (video) => {
    const src = video.dataset.src;
    if (!src) return false;
    if (video.dataset.loaded === '1') return true;
    video.src = src;
    video.dataset.loaded = '1';
    video.load();
    return true;
  };

  const unloadSource = (video) => {
    if (video.dataset.loaded !== '1') return;
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.dataset.loaded = '0';
  };

  const initSection = (section) => {
    if (!section || section.dataset.eldraIgReady === '1') return;
    section.dataset.eldraIgReady = '1';

    const previewSeconds = Math.max(
      2,
      Math.min(6, parseFloat(section.dataset.previewSeconds || '3') || 3)
    );
    const maxPlaying = Math.max(
      1,
      Math.min(2, parseInt(section.dataset.maxPlaying || '1', 10) || 1)
    );

    const rail = section.querySelector('[data-eldra-ig-rail]');
    const prevBtn = section.querySelector('[data-eldra-ig-prev]');
    const nextBtn = section.querySelector('[data-eldra-ig-next]');
    const cards = Array.from(section.querySelectorAll('[data-eldra-ig-card]'));
    if (!cards.length) return;

    const players = cards
      .map((card) => {
        const video = card.querySelector('video.eldra-ig-reels__video');
        if (!video) return null;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.controls = false;
        video.loop = false;
        video.preload = 'none';
        return { card, video, visible: false };
      })
      .filter(Boolean);

    /* Reveal */
    if ('IntersectionObserver' in window && !reduceMotion()) {
      const revealObs = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
      );
      cards.forEach((card, i) => {
        card.style.transitionDelay = `${Math.min(i * 50, 250)}ms`;
        revealObs.observe(card);
      });
    } else {
      cards.forEach((card) => card.classList.add('is-visible'));
    }

    /* Nav arrows */
    const syncNav = () => {
      if (!rail || !prevBtn || !nextBtn) return;
      const maxScroll = rail.scrollWidth - rail.clientWidth - 4;
      const left = rail.scrollLeft;
      prevBtn.hidden = left <= 4;
      nextBtn.hidden = left >= maxScroll;
    };

    const swipeHint = section.querySelector('[data-eldra-ig-swipe-hint]');
    const hideSwipeHint = () => {
      if (!swipeHint || swipeHint.classList.contains('is-hidden')) return;
      swipeHint.classList.add('is-hidden');
    };

    if (rail && prevBtn && nextBtn) {
      const scrollByCard = (dir) => {
        const card = cards[0];
        const step = card ? card.getBoundingClientRect().width + 12 : 200;
        rail.scrollBy({ left: dir * step, behavior: reduceMotion() ? 'auto' : 'smooth' });
      };
      prevBtn.addEventListener('click', () => scrollByCard(-1));
      nextBtn.addEventListener('click', () => scrollByCard(1));
      rail.addEventListener(
        'scroll',
        () => {
          syncNav();
          if (rail.scrollLeft > 8) hideSwipeHint();
        },
        { passive: true }
      );
      window.addEventListener('resize', syncNav, { passive: true });
      syncNav();
    }

    if (!players.length || reduceMotion()) return;

    let playingCount = 0;

    const onTimeUpdate = (player) => {
      if (player.video.currentTime >= previewSeconds) {
        try {
          player.video.currentTime = 0.05;
        } catch (_) {}
      }
    };

    const playPlayer = (player) => {
      if (player.card.classList.contains('is-playing')) return;
      if (playingCount >= maxPlaying) return;
      if (!ensureSource(player.video)) return;

      const run = player.video.play();
      const mark = () => {
        if (!player.card.classList.contains('is-playing')) {
          player.card.classList.add('is-playing');
          playingCount += 1;
        }
      };
      if (run && typeof run.then === 'function') {
        run.then(mark).catch(() => {
          player.card.classList.remove('is-playing');
        });
      } else {
        mark();
      }
    };

    const pausePlayer = (player, unload) => {
      const wasPlaying = player.card.classList.contains('is-playing');
      player.video.pause();
      player.card.classList.remove('is-playing');
      if (wasPlaying && playingCount > 0) playingCount -= 1;
      if (unload) unloadSource(player.video);
    };

    players.forEach((player) => {
      player.video.addEventListener('timeupdate', () => onTimeUpdate(player), { passive: true });
    });

    const refillPlaySlots = () => {
      if (playingCount >= maxPlaying) return;
      const candidates = players.filter(
        (p) => p.visible && !p.card.classList.contains('is-playing')
      );
      candidates.forEach((p) => {
        if (playingCount < maxPlaying) playPlayer(p);
      });
    };

    if (!('IntersectionObserver' in window)) {
      players.slice(0, maxPlaying).forEach((p) => {
        p.visible = true;
        playPlayer(p);
      });
      return;
    }

    /* Hydrate/play near viewport; unload when far */
    const nearObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const player = players.find((p) => p.card === entry.target);
          if (!player) return;
          player.visible = entry.isIntersecting && entry.intersectionRatio >= 0.4;
          if (player.visible) {
            ensureSource(player.video);
            playPlayer(player);
          } else {
            pausePlayer(player, false);
          }
        });
        refillPlaySlots();
      },
      { threshold: [0, 0.4, 0.7], root: null, rootMargin: '40px 0px' }
    );

    const farObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const player = players.find((p) => p.card === entry.target);
          if (!player) return;
          if (!entry.isIntersecting) {
            pausePlayer(player, true);
            player.visible = false;
          }
        });
      },
      { threshold: 0, root: null, rootMargin: '280px 0px' }
    );

    players.forEach((player) => {
      nearObs.observe(player.card);
      farObs.observe(player.card);
    });

    /* Page visibility: pause all when tab hidden */
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          players.forEach((p) => pausePlayer(p, false));
        } else {
          refillPlaySlots();
        }
      },
      { passive: true }
    );
  };

  const boot = () => {
    document.querySelectorAll('[data-eldra-ig-reels]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.('[data-eldra-ig-reels]') || event.target;
    if (section?.matches?.('[data-eldra-ig-reels]')) {
      section.dataset.eldraIgReady = '';
      initSection(section);
    }
  });
})();
